import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { List } from 'immutable';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { ContextMenu, MenuItem, ContextMenuTrigger } from 'react-contextmenu';

import FormSubmitOnBlur from '../../../helpers/FormSubmitOnBlur';
import uniqueId from '../../../../utils/uniqueId';
import { unwrap } from '../../../../utils/ChangeSpy';
import BaseInput from '../../../helpers/Input';
import BaseTextarea from '../../../helpers/Textarea';
import AutoComplete from '../../../helpers/AutoComplete';
import FormGroup from '../../../helpers/FormGroup';
import LoadAnimation from '../../../decorators/LoadAnimation';
import PrettyError from '../../../decorators/PrettyError';
import DisplayError from '../../../decorators/DisplayError';
import { Collapse } from '../../../helpers/Collapse';
import Button from '../../../helpers/Button';

import style from './style.module.css';

const Input = PrettyError(DisplayError(BaseInput));
const Textarea = PrettyError(DisplayError(BaseTextarea));

const yearMap = {
  value: {
    '-1': '?',
  },
};

const renderSuggestion = suggestion => <span>{suggestion.text}</span>;

const ContextMenuWrapper = onClick => {
  const ContextMenuWrapped = (value, data) => {
    const id = uniqueId();

    return (
      <div>
        <ContextMenuTrigger id={id}>{value}</ContextMenuTrigger>

        <ContextMenu id={id}>
          <MenuItem data={data.toJS()} onClick={onClick}>
            <i className="fa fa-remove" /> Delete
          </MenuItem>
        </ContextMenu>
      </div>
    );
  };

  return ContextMenuWrapped;
};

const ContactExecutivePosition = props => {
  const { position, prefix, onChange, onDelete, onClick, id = uniqueId(), suggestTitles, suggestions } = props;

  const isOpened = position.get('isOpened');
  const companyName = position.get('companyName', '');
  const companyType = position.get('companyType', '');
  const title = position.get('title', '');
  const pnl = position.get('pnl', '');
  const pnlEbitda = position.get('pnlEbitda', '');
  const startYear = position.get('startYear', '');
  const startYearValue = unwrap(position.get('startYear')) || '?';
  const endYear = position.get('endYear');
  const endYearValue = (function() {
    const year = unwrap(position.get('endYear'));

    if (year in yearMap.value) return yearMap.value[year];

    return year || 'current';
  })();

  const companyBio = position.get('companyBio', '');

  let companyNameContent = companyName;

  if (isOpened) {
    companyNameContent = (
      <Link target="_blank" to={`/company/${position.get('entityId')}`}>
        {companyNameContent}
      </Link>
    );
  }

  const header = ContextMenuWrapper(onDelete)(
    <div className="panel-heading" onClick={onClick}>
      <div>
        <span className="pull-right mt15">
          <span>
            <b>
              {startYearValue} - {endYearValue}
            </b>
          </span>
        </span>
        <h4 className="panel-title">{companyNameContent}</h4>
        <i>{companyType}</i>
        <div>{unwrap(title)}</div>
      </div>
    </div>,
    position,
  );

  const autocompleteName = `${prefix}.title`;
  const onUpdate = (...params) => suggestTitles.onUpdate(autocompleteName, ...params);
  const onSelect = (...params) => suggestTitles.onSelect(autocompleteName, ...params);

  return (
    <div className="panel panel-default">
      {header}
      <Collapse className={classNames('panel-collapse', { in: isOpened })} id={id} isOpen={isOpened}>
        <div className="panel-body">
          <div className="row">
            <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
              <FormGroup>
                <AutoComplete
                  checkOn="text"
                  formGroupClass=" "
                  getSuggestion={(...rest) => {
                    suggestTitles.onFetch({ ...rest, id: position.get('id') });
                  }}
                  getSuggestionValue={suggestTitles.getValue}
                  inputProps={{
                    className: 'form-control input-sm',
                    label: 'Title',
                    placeholder: 'Title',
                    name: autocompleteName,
                  }}
                  loading={position.get('loading', false)}
                  onSuggestionSelected={onSelect}
                  onUpdateSuggestions={onUpdate}
                  renderSuggestion={renderSuggestion}
                  suggestions={suggestions}
                  text={title}
                  suggestIfEmpty
                />
              </FormGroup>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
              <FormGroup>
                <Input
                  className="input-sm"
                  label="P&amp;L-Revenue"
                  name={`${prefix}.pnl`}
                  onChange={onChange}
                  placeholder="P&amp;L-Revenue"
                  type="number"
                  value={pnl}
                  showLabel
                />
              </FormGroup>
            </div>
            <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
              <FormGroup>
                <Input
                  className="input-sm"
                  label="P&amp;L-EBITDA"
                  name={`${prefix}.pnlEbitda`}
                  onChange={onChange}
                  placeholder="P&amp;L-EBITDA"
                  type="number"
                  value={pnlEbitda}
                  showLabel
                />
              </FormGroup>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
              <FormGroup>
                <Input
                  className="input-sm"
                  label="Start"
                  name={`${prefix}.startYear`}
                  onChange={onChange}
                  placeholder="Start"
                  value={startYear}
                  showLabel
                />
              </FormGroup>
            </div>
            <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
              <FormGroup>
                <Input
                  className="input-sm"
                  displayMap={yearMap}
                  label="End"
                  name={`${prefix}.endYear`}
                  onChange={onChange}
                  placeholder="End"
                  value={endYear}
                  showLabel
                />
              </FormGroup>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
              <FormGroup>
                <Textarea
                  label="Company Bio"
                  name={`${prefix}.companyBio`}
                  onChange={onChange}
                  placeholder="Company Bio"
                  value={companyBio}
                  showLabel
                />
              </FormGroup>
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

const ContactExecutivePositions = ({
  positions,
  onChange,
  suggestions,
  suggestTitles,
  onDelete,
  onClick,
  onSubmit,
  onBlur,
  addPosition,
}) => {
  const positionsContent = positions.map((position, i) => (
    <ContactExecutivePosition
      key={position.get('id')}
      onChange={onChange}
      onClick={event => onClick(event, i)}
      onDelete={onDelete}
      position={position}
      prefix={`positions.${i}`}
      suggestions={suggestions}
      suggestTitles={suggestTitles}
    />
  ));

  return (
    <div className={classNames(style.host, 'accordion-right-side')}>
      <FormSubmitOnBlur onComponentBlur={onBlur} onSubmit={onSubmit}>
        {positionsContent}
      </FormSubmitOnBlur>
      <Button className="btn-primary btn-block" onClick={addPosition}>
        + Add position
      </Button>
    </div>
  );
};

ContactExecutivePositions.propTypes = {
  positions: PropTypes.instanceOf(List).isRequired,
};

export default LoadAnimation(memo(ContactExecutivePositions));
